import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";

import { AuthContext } from "../../../Auth/Context";
import useWexClient from "../../../hooks/Clients/wexClient,";
import HeatMap from "./heatmap";
import Calendario from "../../../components/atoms/Calendario";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MapIcon from "@mui/icons-material/Map";
import BarChartIcon from "@mui/icons-material/BarChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsightsIcon from "@mui/icons-material/Insights";

function HeatMapDashboard({ selectedOption }) {
  const { payload } = useContext(AuthContext) || {};
  const { getIncidentsMapReport, getBases } = useWexClient();

  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showPanel, setShowPanel] = useState(true);
  // para los markers de bases
  const [bases, setBases] = useState([]);

  const [startHour, setStartHour] = useState("00:00");
  const [endHour, setEndHour] = useState("24:00");
  const hours = Array.from(
    { length: 25 },
    (_, i) => i.toString().padStart(2, "0") + ":00",
  );

  const [selectedDays, setSelectedDays] = useState([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ]);

  const handleDayChange = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((item) => item !== day) : [...prev, day],
    );
  };

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  useEffect(() => {
    const fetchHeatmapData = async () => {
      try {
        const serviceId =
          selectedOption || payload?.service || payload?.services?.[0];

        if (!serviceId) return;

        const formatDate = (date) => {
          if (!date) return "";

          const d = new Date(date);
          const year = d.getFullYear();
          const month = `${d.getMonth() + 1}`.padStart(2, "0");
          const day = `${d.getDate()}`.padStart(2, "0");

          return `${year}-${month}-${day}`;
        };

        const body = {
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          service: serviceId,
          days: selectedDays,
          startHour: Number(startHour.split(":")[0]),
          endHour: Number(endHour.split(":")[0]),
        };

        const response = await getIncidentsMapReport(body);

        if (response?.ok && response?.data?.length === 0) {
          setUsers([]);
          setAlert({
            open: true,
            message: "No se encontraron incidencias.",
            severity: "warning",
          });
          return;
        }

        const incidents = response?.data || [];

        const mappedIncidents = Array.isArray(incidents)
          ? incidents.map((inc) => ({
              ...inc,
              location: inc.location || inc.userLocation || inc.ubicationClient,
              date: inc.createdAt || inc.closeAt,
            }))
          : [];

        setUsers(mappedIncidents);
      } catch (error) {
        console.error("Error fetching Heatmap Dashboard:", error);
        setUsers([]);
      }
    };

    fetchHeatmapData();
  }, [
    selectedOption,
    payload?.service,
    payload?.services,
    startDate,
    endDate,
    startHour,
    endHour,
    selectedDays,
  ]);

  const fetchBasesLocations = async () => {
    const serviceId = payload?.service;
    if (!serviceId) {
      return;
    }

    try {
      const data = await getBases({ serviceId });

      if (data?.success === true) {
        const mappedBases = data.data
          .filter((base) => base.status === true && base.coordinates)
          .map((base) => {
            const [lat, lng] = base.coordinates.split(",").map(Number);
            const isConfirmed =
              base?.isConfirmed === true ||
              String(base?.isConfirmed).toLowerCase() === "true";

            return {
              id: base._id,
              lat,
              lng,
              description: base.description,
              isConfirmed,
              status: base.status,
              _id: base._id,
            };
          });
        setBases(mappedBases);
      } else {
        console.error("HeatMapDashboard -> getBases failed", data?.message);
      }
    } catch (error) {
      console.error("HeatMapDashboard -> error en fetchBasesLocations", error);
    }
  };

  useEffect(() => {
    fetchBasesLocations();
  }, [payload?.service]);

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={() =>
          setAlert((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setAlert((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Stack direction="row" sx={{ width: "100%", minHeight: "650px" }}>
        {/* PANEL IZQUIERDO */}
        {showPanel && (
          <Stack
            sx={{
              width: "42%",
              minWidth: "380px",
              padding: "2rem",
              boxSizing: "border-box",
              background: "#ffffff",
              gap: 2.5,
            }}
          >
            {/* ENCABEZADO */}
            <Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MapIcon
                  sx={{
                    color: "#0c4a91",
                    fontSize: "2rem",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "1.8rem",
                    fontWeight: 700,
                    color: "#0c4a91",
                    mb: 0.5,
                  }}
                >
                  Heatmap de Incidencias
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontSize: "0.95rem",
                  color: "#0c4a91",
                }}
              >
                Análisis visual de concentración por periodo
              </Typography>
            </Stack>

            {/* RESUMEN */}
            <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
              <Stack
                sx={{
                  flex: 1,
                  backgroundColor: "white",
                  borderRadius: "18px",
                  p: 2,
                  boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
                  borderTop: "5px solid #0c4a91",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <BarChartIcon
                  sx={{
                    color: "#2563eb",
                    fontSize: "1.8rem",
                    mb: 0.5,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#6b7280",
                    mb: 0.5,
                  }}
                >
                  Siniestros Atendidos
                </Typography>

                <Typography
                  sx={{
                    fontSize: "2.4rem",
                    fontWeight: 700,
                    color: "#0c4a91",
                    lineHeight: 1,
                  }}
                >
                  {users.length}
                </Typography>
              </Stack>
            </Stack>

            {/* FILTROS */}
            <Stack
              sx={{
                backgroundColor: "white",
                borderRadius: "20px",
                p: 2.2,
                boxShadow: "0 10px 25px rgba(0,0,0,0.14)",
                border: "1px solid rgba(12, 74, 145, 0.08)",
              }}
            >
              {/* ENCABEZADO FILTROS */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SearchIcon
                    sx={{
                      color: "#0c4a91",
                      fontSize: "1.25rem",
                    }}
                  />

                  <Stack>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 800,
                        color: "#0c4a91",
                      }}
                    >
                      Filtros de búsqueda
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color: "#777",
                        mt: 0.2,
                      }}
                    >
                      Selecciona el periodo que deseas analizar
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              {/* CONTENEDOR DEL CALENDARIO */}
              <Stack
                sx={{
                  backgroundColor: "#4682cf",
                  borderRadius: "14px",
                  p: 1.5,
                  border: "1px solid #e4edf7",
                }}
              >
                <Calendario
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                />
              </Stack>

              {/* FILTRO DE HORARIO */}
              <Stack sx={{ mt: 1.5, px: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.8}
                  sx={{
                    mb: 1.5,
                    mt: 0.5,
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      color: "#0c4a91",
                      fontSize: "1.25rem",
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#0c4a91",
                      fontSize: "1.2rem",
                      fontWeight: 800,
                    }}
                  >
                    Horario de análisis
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Hora inicio</InputLabel>
                    <Select
                      value={startHour}
                      label="Hora inicio"
                      onChange={(e) => setStartHour(e.target.value)}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        height: "42px",
                        fontSize: "0.85rem",
                      }}
                    >
                      {hours.map((hour) => (
                        <MenuItem key={hour} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Hora final</InputLabel>
                    <Select
                      value={endHour}
                      label="Hora final"
                      onChange={(e) => setEndHour(e.target.value)}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        height: "42px",
                        fontSize: "0.85rem",
                      }}
                    >
                      {hours.map((hour) => (
                        <MenuItem key={hour} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              {/* FILTRO DE DÍAS */}
              <Stack
                sx={{
                  mt: 2,
                  backgroundColor: "#f5f7fa",
                  borderRadius: "16px",
                  p: 2,
                  border: "1px solid #dfe6ee",
                }}
              >
                {/* ENCABEZADO */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#e5effc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CalendarMonthIcon
                      sx={{
                        color: "#0c4a91",
                        fontSize: "1.35rem",
                      }}
                    />
                  </Box>

                  <Stack>
                    <Typography
                      sx={{
                        color: "#0c4a91",
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      Días de la semana
                    </Typography>

                    <Typography
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.78rem",
                      }}
                    >
                      Selecciona los días que deseas analizar
                    </Typography>
                  </Stack>
                </Stack>

                {/* DÍAS */}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    mt: 1.5,
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  {[
                    ["monday", "Lun"],
                    ["tuesday", "Mar"],
                    ["wednesday", "Mié"],
                    ["thursday", "Jue"],
                    ["friday", "Vie"],
                    ["saturday", "Sáb"],
                    ["sunday", "Dom"],
                  ].map(([value, label]) => {
                    const selected = selectedDays.includes(value);

                    return (
                      <Box
                        key={value}
                        onClick={() => handleDayChange(value)}
                        sx={{
                          width: "64px",
                          height: "58px",
                          borderRadius: "10px",

                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",

                          cursor: "pointer",
                          userSelect: "none",

                          backgroundColor: selected ? "#0c4a91" : "#ffffff",

                          color: selected ? "white" : "#5f6b7a",

                          border: selected
                            ? "1px solid #0c4a91"
                            : "1px solid #d8e1eb",

                          boxShadow: selected
                            ? "0 3px 8px rgba(12,74,145,0.20)"
                            : "0 1px 3px rgba(0,0,0,0.05)",

                          transition: "all 0.2s ease",

                          "&:hover": {
                            transform: "translateY(-2px)",
                            borderColor: "#0c4a91",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            mb: 0.7,
                            color: "inherit",
                          }}
                        >
                          {selected ? "✓" : "○"}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            color: "inherit",
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Stack>

              {/* PERIODO ACTIVO */}
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  mt: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: "10px",
                  backgroundColor: "rgba(12, 74, 145, 0.07)",
                  borderLeft: "4px solid #0c4a91",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#555",
                  }}
                >
                  Las incidencias del mapa corresponden al periodo seleccionado.
                </Typography>
              </Stack>
            </Stack>

            {/* LEYENDA */}
            <Stack
              sx={{
                backgroundColor: "white",
                borderRadius: "18px",
                p: 2,
                boxShadow: "0 10px 24px rgba(0,0,0,0.13)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0c4a91",
                  mb: 1.5,
                }}
              >
                Intensidad de incidencias
              </Typography>

              <Stack
                direction="row"
                sx={{
                  height: "12px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#42e66c",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#d7ed49",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#ffd740",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#ff9800",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#f44336",
                  }}
                />
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#777",
                  }}
                >
                  Baja
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#777",
                  }}
                >
                  Alta
                </Typography>
              </Stack>
            </Stack>

            {/* AYUDA */}
            <Stack
              sx={{
                backgroundColor: "rgba(49, 130, 223, 0.49)",
                borderRadius: "16px",
                p: 2,
                borderLeft: "5px solid white",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "white",
                  mb: 0.7,
                }}
              >
                Cómo usar el mapa
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.88rem",
                  color: "white",
                  lineHeight: 1.6,
                }}
              >
                Selecciona una zona del Heatmap para visualizar la cantidad de
                incidencias registradas dentro del radio de análisis.
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* MAPA */}
        <Stack
          sx={{
            width: showPanel ? "58%" : "100%",
            minHeight: "650px",
            position: "relative",
            transition: "width 0.3s ease",
          }}
        >
          <IconButton
            onClick={() => setShowPanel(!showPanel)}
            sx={{
              position: "absolute",
              top: "20px",
              left: "20px",
              zIndex: 10,
              backgroundColor: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {showPanel ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          <HeatMap
            serviceId={payload.service}
            users={users}
            bases={bases}
            onBasesChange={fetchBasesLocations}
          />
        </Stack>
      </Stack>
    </>
  );
}

export default HeatMapDashboard;
